import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { theme } from "theme";

const MOBILE_BREAKPOINT = parseInt(theme.breakpoint, 10);
const MIN_WIDTH = 400;
const MIN_HEIGHT = 300;

interface Size {
  width: number;
  height: number;
}

interface Position {
  x: number;
  y: number;
}

type DragMode = "move" | "resize" | null;

export const useDraggable = () => {
  const [position, setPosition] = useState<Position | null>(null);
  const [size, setSize] = useState<Size | null>(null);
  const mode = useRef<DragMode>(null);
  const dragOffset = useRef<Position>({ x: 0, y: 0 });
  const startPos = useRef<Position>({ x: 0, y: 0 });
  const startSize = useRef<Size>({ width: 0, height: 0 });

  const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

  const reset = useCallback(() => {
    setPosition(null);
    setSize(null);
    mode.current = null;
  }, []);

  // Reset on mobile, re-clamp position/size on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        reset();
        return;
      }

      setSize(prev => {
        if (!prev) return prev;
        return {
          width: Math.min(prev.width, window.innerWidth),
          height: Math.min(prev.height, window.innerHeight),
        };
      });

      setPosition(prev => {
        if (!prev) return prev;
        const w = size?.width ?? 480;
        const h = size?.height ?? 640;
        return {
          x: Math.max(0, Math.min(prev.x, window.innerWidth - w)),
          y: Math.max(0, Math.min(prev.y, window.innerHeight - h)),
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [reset, size]);

  const clampPosition = useCallback(
    (pos: Position, w: number, h: number): Position => ({
      x: Math.max(0, Math.min(pos.x, window.innerWidth - w)),
      y: Math.max(0, Math.min(pos.y, window.innerHeight - h)),
    }),
    []
  );

  const onContainerPointerDown = useCallback((e: React.PointerEvent) => {
    if (isMobile()) return;

    const target = e.target as HTMLElement;
    if (target.closest("button, a, input, select")) return;

    mode.current = "move";

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onResizePointerDown = useCallback((e: React.PointerEvent) => {
    if (isMobile()) return;

    mode.current = "resize";
    startPos.current = { x: e.clientX, y: e.clientY };

    const container = (e.currentTarget as HTMLElement).parentElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      startSize.current = { width: rect.width, height: rect.height };
    }

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const onContainerPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (mode.current !== "move") return;

      const currentRect = (e.currentTarget as HTMLElement).getBoundingClientRect();

      setPosition(
        clampPosition(
          { x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y },
          currentRect.width,
          currentRect.height
        )
      );
    },
    [clampPosition]
  );

  const onResizePointerMove = useCallback((e: React.PointerEvent) => {
    if (mode.current !== "resize") return;

    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;

    const containerRect = (e.currentTarget as HTMLElement).parentElement?.getBoundingClientRect();
    const maxWidth = window.innerWidth - (containerRect?.left ?? 0);
    const maxHeight = window.innerHeight - (containerRect?.top ?? 0);

    const newWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, startSize.current.width + dx));
    const newHeight = Math.max(MIN_HEIGHT, Math.min(maxHeight, startSize.current.height + dy));

    setSize({ width: newWidth, height: newHeight });
  }, []);

  const onPointerUp = useCallback(() => {
    mode.current = null;
  }, []);

  const style: React.CSSProperties = {
    ...(!isMobile() ? { cursor: "grab" } : {}),
    ...(position ? { left: position.x, top: position.y, right: "auto", bottom: "auto" } : {}),
    ...(size ? { width: size.width, height: size.height } : {}),
  };

  const containerProps = !isMobile()
    ? {
        onPointerDown: onContainerPointerDown,
        onPointerMove: onContainerPointerMove,
        onPointerUp,
        style,
      }
    : {};

  const resizeHandleProps = {
    onPointerDown: onResizePointerDown,
    onPointerMove: onResizePointerMove,
    onPointerUp,
  };

  return { containerProps, resizeHandleProps, reset };
};
